import { useEffect, useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import Header from "@/components/layout/Header"
import Footer from "@/components/layout/Footer"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
// 💡 방금 만든 update, delete API 임포트 추가!
import { getCart, updateCartItemQuantity, deleteCartItem } from "@/api/cartApi"
import { useAuth } from "@/contexts/AuthContext"

interface CartItem {
    cartItemId: number
    productId: number
    productName: string
    price: number
    thumbnailUrl: string
    quantity: number
}

export default function CartPage() {
    const navigate = useNavigate()
    const { isLoggedIn, isLoading } = useAuth()

    const [cartItems, setCartItems] = useState<CartItem[]>([])
    const [pageLoading, setPageLoading] = useState(true)

    // 장바구니 조회 함수 (재사용을 위해 분리)
    const fetchCartItems = async () => {
        try {
            const response = await getCart()
            setCartItems(response.data || [])
        } catch (error) {
            console.error("장바구니 조회 실패:", error)
        } finally {
            setPageLoading(false)
        }
    }

    useEffect(() => {
        if (isLoading) return
        if (!isLoggedIn) {
            alert("로그인이 필요한 서비스입니다.")
            navigate("/login")
            return
        }
        fetchCartItems()
    }, [isLoggedIn, isLoading, navigate])

    // 💡 1. 수량 변경 핸들러
    const handleQuantityChange = async (cartItemId: number, currentQuantity: number, type: "increase" | "decrease") => {
        const newQuantity = type === "increase" ? currentQuantity + 1 : currentQuantity - 1

        if (newQuantity < 1) {
            alert("수량은 1개 이상이어야 합니다.")
            return
        }

        try {
            // 1) 낙관적 업데이트 (서버 응답 전 UI 먼저 빠르게 변경)
            setCartItems(prev => prev.map(item =>
                item.cartItemId === cartItemId ? { ...item, quantity: newQuantity } : item
            ))

            // 2) 백엔드 API 호출
            await updateCartItemQuantity(cartItemId, newQuantity)
        } catch (error) {
            console.error("수량 변경 실패:", error)
            alert("수량 변경에 실패했습니다.")
            fetchCartItems() // 실패 시 원래 데이터로 롤백
        }
    }

    // 💡 2. 삭제 핸들러
    const handleDelete = async (cartItemId: number) => {
        if (!confirm("장바구니에서 삭제하시겠습니까?")) return

        try {
            await deleteCartItem(cartItemId)
            // 삭제 성공 시 목록 새로고침
            fetchCartItems()
        } catch (error) {
            console.error("삭제 실패:", error)
            alert("삭제에 실패했습니다.")
        }
    }

    const totalProductPrice = cartItems.reduce((acc, item) => acc + (item.price * item.quantity), 0)
    const deliveryFee = cartItems.length === 0 ? 0 : (totalProductPrice >= 50000 ? 0 : 3000)
    const totalPaymentPrice = totalProductPrice + deliveryFee

    if (pageLoading || isLoading) {
        return (
            <div className="min-h-screen bg-slate-50">
                <Header />
                <div className="flex h-[50vh] items-center justify-center text-slate-500">장바구니를 불러오는 중입니다...</div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900">
            <Header />

            <main className="mx-auto max-w-7xl px-4 py-8 lg:px-6">
                <h1 className="mb-8 text-3xl font-bold">장바구니</h1>

                <div className="grid gap-8 lg:grid-cols-3 lg:items-start">
                    <div className="lg:col-span-2 space-y-4">
                        {cartItems.length === 0 ? (
                            <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                                <CardContent className="flex flex-col items-center justify-center py-20">
                                    <div className="mb-4 h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center text-2xl">🛒</div>
                                    <p className="text-lg font-medium text-slate-900">장바구니에 담긴 상품이 없습니다.</p>
                                    <p className="mt-1 text-sm text-slate-500">원하는 상품을 장바구니에 담아보세요!</p>
                                    <Button asChild className="mt-6 rounded-full bg-slate-900 px-6 text-white hover:bg-slate-800">
                                        <Link to="/">쇼핑 계속하기</Link>
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            cartItems.map((item) => (
                                <Card key={item.cartItemId} className="rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                                    <CardContent className="p-5 sm:p-6">
                                        <div className="flex gap-4 sm:gap-6">
                                            <div className="h-24 w-24 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-100 sm:h-32 sm:w-32 cursor-pointer" onClick={() => navigate(`/product/${item.productId}`)}>
                                                <img src={item.thumbnailUrl} alt={item.productName} className="h-full w-full object-cover" />
                                            </div>

                                            <div className="flex flex-1 flex-col justify-between">
                                                <div>
                                                    <h3 className="text-base font-semibold leading-tight text-slate-900 sm:text-lg hover:underline cursor-pointer" onClick={() => navigate(`/product/${item.productId}`)}>
                                                        {item.productName}
                                                    </h3>

                                                    {/* 💡 수량 조절 버튼 UI */}
                                                    <div className="mt-3 flex items-center w-[100px] rounded-lg border border-slate-200 bg-slate-50">
                                                        <button
                                                            onClick={() => handleQuantityChange(item.cartItemId, item.quantity, "decrease")}
                                                            className="flex h-8 w-8 items-center justify-center text-slate-600 hover:bg-slate-200 rounded-l-lg"
                                                        >-</button>
                                                        <span className="flex-1 text-center text-sm font-medium">{item.quantity}</span>
                                                        <button
                                                            onClick={() => handleQuantityChange(item.cartItemId, item.quantity, "increase")}
                                                            className="flex h-8 w-8 items-center justify-center text-slate-600 hover:bg-slate-200 rounded-r-lg"
                                                        >+</button>
                                                    </div>
                                                </div>

                                                <div className="mt-4 flex items-end justify-between">
                                                    <span className="text-lg font-bold text-slate-900 sm:text-xl">
                                                        {(item.price * item.quantity).toLocaleString("ko-KR")}원
                                                    </span>
                                                    {/* 💡 삭제 버튼 UI */}
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleDelete(item.cartItemId)}
                                                        className="h-8 rounded-lg text-xs hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200"
                                                    >
                                                        삭제
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))
                        )}
                    </div>

                    {/* 우측 결제 요약 영역 (기존과 동일) */}
                    <div className="sticky top-28">
                        <Card className="rounded-[28px] border border-slate-200 bg-white shadow-sm">
                            <CardContent className="p-6">
                                <h2 className="text-lg font-bold text-slate-900">결제 정보</h2>

                                <div className="mt-6 space-y-4 text-sm text-slate-600">
                                    <div className="flex justify-between">
                                        <span>총 상품금액</span>
                                        <span className="font-semibold text-slate-900">{totalProductPrice.toLocaleString("ko-KR")}원</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>배송비</span>
                                        <span className="font-semibold text-slate-900">
                                            {deliveryFee === 0 ? "무료" : `+${deliveryFee.toLocaleString("ko-KR")}원`}
                                        </span>
                                    </div>
                                    {totalProductPrice > 0 && totalProductPrice < 50000 && (
                                        <p className="text-right text-xs text-indigo-500">
                                            {(50000 - totalProductPrice).toLocaleString("ko-KR")}원 추가 주문 시 무료배송
                                        </p>
                                    )}
                                </div>

                                <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-6">
                                    <span className="text-base font-bold text-slate-900">총 결제금액</span>
                                    <span className="text-2xl font-bold text-indigo-600">
                                        {totalPaymentPrice.toLocaleString("ko-KR")}원
                                    </span>
                                </div>

                                <Button
                                    className="mt-6 h-14 w-full rounded-2xl bg-indigo-600 text-base font-bold text-white hover:bg-indigo-700 disabled:bg-slate-300 disabled:text-slate-500"
                                    disabled={cartItems.length === 0}
                                    onClick={() => navigate("/order")} // 주문 페이지 이동으로 연결
                                >
                                    주문하기 ({cartItems.length}개)
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    )
}